import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOrderWizardContext } from '../OrderWizardProvider';

const BasicInformationStep = ({ clients, businessUnits, loadingClients, validationErrors, onClientChange }) => {
  const { orderData, updateOrderData, isEditMode } = useOrderWizardContext();
  // En modo edición, cargar las cuentas automáticamente si hay un cliente seleccionado
  useEffect(() => {
    if (isEditMode && orderData.clienteId && onClientChange) {
      // Llamar inmediatamente sin debounce en modo edición
      onClientChange(orderData.clienteId);
    }
  }, [isEditMode, orderData.clienteId, onClientChange]);

  const handleClientChange = (clientId) => {
    const selectedClient = clients.find(c => String(c.ClientID) === String(clientId));
    
    updateOrderData({
      clienteId: String(clientId),
      clienteName: selectedClient ? selectedClient.ClientName : ''
    });
    
    if (onClientChange) {
      onClientChange(clientId);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h5 className="text-primary mb-2">
          <FontAwesomeIcon icon="info-circle" className="me-2" />
          Información Básica
        </h5>
        <p className="text-muted mb-0">
          Ingresa los datos fundamentales de la orden de inserción
        </p>
      </div>

      <Row className="g-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-semibold">
              <FontAwesomeIcon icon="user" className="me-1 text-primary" />
              Cliente *
            </Form.Label>
            <Form.Select
              value={orderData.clienteId}
              onChange={(e) => {
                handleClientChange(e.target.value);
              }}
              disabled={loadingClients}
              className={validationErrors.clienteId ? 'is-invalid' : ''}
              required
            >
              <option value="">
                {loadingClients ? 'Cargando clientes...' : 'Seleccionar cliente...'}
              </option>
              {clients.map(client => {
                const clientIdStr = String(client.ClientID);
                return (
                  <option key={client.ClientID} value={clientIdStr}>
                    {client.ClientName}
                  </option>
                );
              })}
            </Form.Select>
            {validationErrors.clienteId && (
              <div className="invalid-feedback d-block">
                {validationErrors.clienteId}
              </div>
            )}
            <Form.Text className="text-muted">
              Selecciona el cliente para esta orden
            </Form.Text>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-semibold">
              <FontAwesomeIcon icon="cogs" className="me-1 text-info" />
              Business Unit *
            </Form.Label>
            <Form.Select
              value={orderData.businessUnit}
              onChange={(e) => updateOrderData('businessUnit', e.target.value)}
              className={validationErrors.businessUnit ? 'is-invalid' : ''}
              required
            >
              <option value="">Seleccionar Business Unit...</option>
              {businessUnits.map(unit => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </Form.Select>
            {validationErrors.businessUnit && (
              <div className="invalid-feedback d-block">
                {validationErrors.businessUnit}
              </div>
            )}
            <Form.Text className="text-muted">
              Unidad de negocio responsable de esta orden
            </Form.Text>
          </Form.Group>
        </Col>

        <Col xs={12}>
          <Form.Group>
            <Form.Label className="fw-semibold">
              <FontAwesomeIcon icon="edit" className="me-1 text-primary" />
              Descripción de la Orden *
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={orderData.descripcion}
              onChange={(e) => updateOrderData('descripcion', e.target.value)}
              placeholder="Describe el propósito y objetivos de esta orden de inserción..."
              className={validationErrors.descripcion ? 'is-invalid' : ''}
              required
            />
            {validationErrors.descripcion && (
              <div className="invalid-feedback d-block">
                {validationErrors.descripcion}
              </div>
            )}
            <Form.Text className="text-muted">
              Proporciona una descripción clara y detallada de la orden
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      {/* Indicador de progreso */}
      <div className="mt-4 pt-3 border-top">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <small className="text-muted">Paso 1 de 4</small>
          </div>
          <div className="text-end">
            <small className="text-muted">
              Siguiente: Información Financiera
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

BasicInformationStep.propTypes = {
  clients: PropTypes.array.isRequired,
  businessUnits: PropTypes.array.isRequired,
  loadingClients: PropTypes.bool,
  validationErrors: PropTypes.object,
  onClientChange: PropTypes.func
};

export default BasicInformationStep; 